import { Drawer, Flex, Image } from 'antd';
import './style.scss';
import { useEffect, useState } from 'react';
import { isMobileScreenSize } from 'utils/helpers';
import {
  DownloadOutlined,
  LeftOutlined,
  RightOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
} from '@ant-design/icons';
import PDFImage from 'assets/pdf-thumbnail.png';

type CustomFileListProps = {
  open?: boolean;
  images: string[];
  hasDrawer?: boolean;
  drawerTitle?: React.ReactNode;
  drawerContent?: React.ReactNode;
  onClose?: () => void;
};

const CustomFileList = ({
  open,
  images,
  hasDrawer = false,
  drawerTitle,
  drawerContent,
  onClose,
}: CustomFileListProps) => {
  const [openPreview, setOpenPreview] = useState(false);
  const [drawerImageIndex, setDrawerImageIndex] = useState(0);
  const [drawerImageRotation, setDrawerImageRotation] = useState(0);

  const handleChange = (value: boolean) => {
    if (!value) {
      onClose?.();
    }
    setOpenPreview(value);
  };

  const handleNext = () => {
    if (drawerImageIndex + 1 < images.length)
      setDrawerImageIndex(drawerImageIndex + 1);
    else {
      setDrawerImageIndex(0);
    }
    setDrawerImageRotation(0);
  };

  const handlePrev = () => {
    if (drawerImageIndex - 1 >= 0) setDrawerImageIndex(drawerImageIndex - 1);
    else {
      setDrawerImageIndex(images.length - 1);
    }
    setDrawerImageRotation(0);
  };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = images?.[drawerImageIndex];
    a.download = images?.[drawerImageIndex]?.split('/')?.pop() ?? '';
    a.target = '_blank';
    a.rel = 'noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleRotate = (rotation: number) => {
    const newRotation = (drawerImageRotation + rotation) % 360;
    setDrawerImageRotation(newRotation < 0 ? newRotation + 360 : newRotation);
  };

  useEffect(() => {
    if (open) handleChange(true);
  }, [open]);

  return (
    <>
      <Image.PreviewGroup
        preview={{
          rootClassName: hasDrawer ? 'custom-preview' : '',
          visible: !(hasDrawer && isMobileScreenSize) && openPreview,
          onVisibleChange: handleChange,
          imageRender: (originalNode, info) => {
            if (info?.image?.url?.endsWith('.pdf')) {
              return (
                <object
                  data={info?.image?.url}
                  type="application/pdf"
                  width="100%"
                  height="100%"
                >
                  {"Problem showing the file, here's "}
                  <a href={info?.image?.url}>link to the PDF</a>
                </object>
              );
            }
            return originalNode;
          },
        }}
      >
        <Flex gap={8} className="custom-image-list">
          {images?.map((src, index) => (
            <Image key={`image-${index}`} src={src} fallback={PDFImage} />
          ))}
          {images?.length > 3 ? (
            <div className="counter">
              <span>+{images.length - 3}</span>
            </div>
          ) : null}
        </Flex>
      </Image.PreviewGroup>

      {hasDrawer && (
        <Drawer
          title={drawerTitle}
          open={openPreview}
          onClose={() => handleChange(false)}
          mask={false}
          width={500}
          className="custom-drawer-image-preview"
        >
          {isMobileScreenSize ? (
            <Flex
              vertical
              align="center"
              gap={12}
              className="preview-container"
            >
              <Flex
                justify="space-between"
                align="center"
                style={{ width: '100%', overflow: 'hidden' }}
              >
                <div className="image-button" onClick={handlePrev}>
                  <LeftOutlined />
                </div>
                {images?.map((image, index) => (
                  <img
                    key={image + index}
                    className="preview-img"
                    src={image}
                    style={{
                      display: index !== drawerImageIndex ? 'none' : '',
                      transform: `rotate(${drawerImageRotation}deg)`,
                    }}
                  />
                ))}
                <div className="image-button" onClick={handleNext}>
                  <RightOutlined />
                </div>
              </Flex>
              <div>
                {drawerImageIndex + 1}/{images.length}
              </div>
              <div className="ant-image-preview-operations">
                <div
                  className="ant-image-preview-operations-operation"
                  onClick={handleDownload}
                >
                  <DownloadOutlined />
                </div>
                <div
                  className="ant-image-preview-operations-operation"
                  onClick={() => handleRotate(-90)}
                >
                  <RotateLeftOutlined />
                </div>
                <div
                  className="ant-image-preview-operations-operation"
                  onClick={() => handleRotate(90)}
                >
                  <RotateRightOutlined />
                </div>
              </div>
            </Flex>
          ) : null}
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};

export default CustomFileList;
